import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePositionStore } from '../model/position.store';
import { positionApi, type PositionFormData } from '@/entities/Position';
import { toastError, toastSuccess, useCustomToast } from '@/shared/lib';

/**
 * Composable for position management operations
 * Provides high-level methods for creating, updating, and deleting positions
 */
export function usePositionManagement() {
  const store = usePositionStore();
  const router = useRouter();
  const toast = useCustomToast();

  // ===== COMPUTED =====
  const positions = computed(() => store.positions);
  const currentPosition = computed(() => store.currentPosition);
  const isLoading = computed(() => store.isLoading);
  const isCreating = computed(() => store.isCreating);
  const isUpdating = computed(() => store.isUpdating);
  const isDeleting = computed(() => store.isDeleting);
  const error = computed(() => store.error);
  const hasPositions = computed(() => store.hasPositions);
  const activePositions = computed(() => store.activePositions);

  // ===== METHODS =====
  async function loadAllPositions(): Promise<{ success: boolean }> {
    const success = await store.fetchAllPositions();

    if (!success && store.error) {
      toastError(toast, 'Erro ao carregar cargos', store.error || '');
    }

    return { success };
  }

  async function loadPosition(positionId: number | string) {
    return await store.fetchPositionById(positionId);
  }

  async function createPosition(formData: {
    position: string;
    departmentId: number;
  }): Promise<{ success: boolean }> {
    const positionData: PositionFormData = {
      name: formData.position,
      departmentId: formData.departmentId,
      active: true,
    };

    const success = await store.createPosition(positionData);

    if (success) {
      toastSuccess(toast, 'Cargo criado com sucesso');
    } else {
      toastError(toast, store.error || 'Erro ao criar cargo, tente novamente.');
    }

    return { success };
  }

  async function updatePosition(
    positionId: number | string,
    formData: Partial<PositionFormData>
  ): Promise<{ success: boolean }> {
    const success = await store.updatePosition(positionId, formData);

    if (success) {
      toastSuccess(toast, 'Edição salva com sucesso');
    } else {
      toastError(toast, store.error || 'Erro ao salvar edições, tente novamente.');
    }

    return { success };
  }

  async function deletePosition(
    positionId: number,
    showToast: boolean = true
  ): Promise<{ success: boolean }> {
    const success = await store.deletePosition(positionId);

    if (success && showToast) {
      toastSuccess(toast, 'Cargo deletado com sucesso');
    } else if (!success && showToast) {
      toastError(toast, 'Erro ao deletar cargo');
    }

    return { success };
  }

  async function deletePositions(
    positionIds: number[]
  ): Promise<{ success: boolean; deletedCount: number }> {
    if (!positionIds || positionIds.length === 0) {
      return { success: false, deletedCount: 0 };
    }

    const { success, deletedCount } = await store.bulkDeletePositions(positionIds);

    if (deletedCount > 0) {
      const title =
        deletedCount > 1 ? 'Cargos deletados com sucesso' : 'Cargo deletado com sucesso';

      toastSuccess(toast, title);
    } else {
      toastError(toast, 'Erro ao deletar cargos');
    }

    return { success, deletedCount };
  }

  async function getMultipleDeleteDescription(positionIds: number[]): Promise<string> {
    const defaultDescription = 'A ação não pode ser desfeita.';

    if (!positionIds || positionIds.length === 0) {
      return defaultDescription;
    }

    const { hasLinks } = await positionApi.checkLinks(positionIds);

    if (!hasLinks) {
      return defaultDescription;
    }

    return positionIds.length === 1
      ? 'Este cargo possui vínculos com membros, documentos, treinamentos ou permissões. Ao excluí-lo, esses vínculos serão removidos. A ação não pode ser desfeita.'
      : 'Um ou mais cargos selecionados possuem vínculos com membros, documentos, treinamentos ou permissões. Ao excluí-los, esses vínculos serão removidos. A ação não pode ser desfeita.';
  }

  async function clonePermissions(
    sourcePositionId: number,
    targetPositionId: number
  ): Promise<{ success: boolean }> {
    try {
      const { permissionsCount } = await positionApi.clonePermissions(
        sourcePositionId,
        targetPositionId
      );

      if (permissionsCount === 0) {
        toast.showWarningToast({
          title: 'Permissões clonadas',
          message: 'O cargo de origem não possuía permissões: o cargo ficou sem nenhuma permissão.',
          type: 'warning',
        });
      } else {
        toastSuccess(toast, 'Permissões clonadas com sucesso');
      }

      return { success: true };
    } catch (error) {
      console.error('Erro ao clonar permissões:', error);

      const errorMessage =
        error instanceof Error ? error.message : 'Não foi possível clonar as permissões do cargo.';

      toastError(toast, 'Erro ao clonar permissões', errorMessage);

      return { success: false };
    }
  }

  function navigateToList() {
    router.push('/admin/cargos');
  }

  function clearError() {
    store.clearError();
  }

  function reset() {
    store.reset();
  }

  // ===== RETURN =====
  return {
    // State
    positions,
    currentPosition,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    hasPositions,
    activePositions,
    // Methods
    loadAllPositions,
    loadPosition,
    createPosition,
    updatePosition,
    deletePosition,
    deletePositions,
    clonePermissions,
    getMultipleDeleteDescription,
    navigateToList,
    clearError,
    reset,
  };
}
